import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { FieldType, IContentHubType, IEntityColumn, IEntityToken, IProvisionProfile, ISensitivityLabel, ISiteTemplate, IUserConfigPackage, ProvisionType, SecurityMode, SharingCapabilities } from '../../../API';
import { MessageBar } from '@fluentui/react';
import { EntityMultiColumnSelectorState } from '../entity-types/EntityMultiColumnSelector';
import TemplateTriggers, { TemplateTriggersState } from '../template-triggers/TemplateTriggers';
import { ExpressionFieldState } from '../expression-builder/ExpressionField';
import EntityTypeDialogFavouritesTab from '../entity-types/tabs/EntityTypeDialogFavouritesTab';
import { ContentMetadataFieldsListState } from '../content-metadata-fields/ContentMetadataFieldsList';
import AIPropertiesList, { AIPropertiesListState } from '../ai-properties/AIPropertiesList';
import WidgetOutline, { WidgetOutlineState } from '../../controls/WidgetOutline';
import ProfileWorkspaceDetails from './ProfileWorkspaceDetails';
import ProfileWorkspaceSecurity from './ProfileWorkspaceSecurity';
import ProfileNameAndCondition from './ProfileNameAndCondition';
import ProfileWorkspaceMetadata from './ProfileWorkspaceMetadata';
import ProfileItemAttachments from './ProfileItemAttachments';
import { ExpressionBuilderOutputType } from '../expression-builder/ExpressionBuilder';
import Triggers, { TriggersState } from '../triggers/Triggers';
import { EntityTypeEditorState } from '../entity-types/EntityTypeEditor';

export class ProvisionProfileEditorState implements IProvisionProfile {
	
	public userConfig: IUserConfigPackage;
	public showProfileInfo: boolean = false;
	public entityTypePluralName:string = '';
	public listUrl:string = '';
	
	public Id: string = '';
	
	public Name: string = '';
	public ProvisionType: ProvisionType = ProvisionType.Site;
	public SiteTemplate: string = 'STS#3';
	public WorkspaceTitle: string = '';
	public WorkspacePath: string = 'sites/';
	public WorkspacePathComponent: string = '';
	public TemplateTriggerIds: string[] = [];
	public TriggerIds: string[] = [];
	public AIPropertyIds: string[] = [];
	public ContentTypeIds: string[] = [];
	public SiteSharingCapabilities: SharingCapabilities = SharingCapabilities.Disabled;
	public SecurityMode: SecurityMode = SecurityMode.None;
	public SecurityOwnerFields: string[] = [];
	public SecurityVisitorFields: string[] = [];
	public SecurityMemberFields: string[]= [];
	
	public SecurityOwners: string[] = [];
	public SecurityVisitors: string[] = [];
	public SecurityMembers: string[] = [];
	
	public FavouritesPeople: string[] = [];
	
	public MetadataFields: string[] = [];
	public PrivacyExpression: string = '';
	public MetadataPrivacyExpression: string = '';
	public Expression: string = '';
	public CreateWorkspaceExpression: string = '';

	public FavouritesPeopleFields: string[] = [];
	public EnableOneDriveSync: boolean = false;
	public OneDriveSyncPath: string = '';

	public MoveAttachmentsToWorkspace: boolean = false;
	public MoveAttachmentsToWorkspacePath: string = '';

	public SensitivityLabelExpression: string = '';

	public MailNickname: string = '';
	public Description: string = '';

	public MetadataSecurityMode: SecurityMode = SecurityMode.None;
	public MetadataSecurityOwnerFields: string[] = [];
	public MetadataSecurityMemberFields: string[] = [];
	public MetadataSecurityVisitorFields: string[] = [];
	public MetadataSecurityOwners: string[] = [];
	public MetadataSecurityMembers: string[] = [];
	public MetadataSecurityVisitors: string[] = [];

	public ownerFields: EntityMultiColumnSelectorState;
	public memberFields: EntityMultiColumnSelectorState;
	public visitorFields: EntityMultiColumnSelectorState;
	public metadataFields: ContentMetadataFieldsListState;

	public metadataSecurityOwnerFields: EntityMultiColumnSelectorState;
	public metadataSecurityMemberFields: EntityMultiColumnSelectorState;
	public metadataSecurityVisitorFields: EntityMultiColumnSelectorState;

	public templateTriggers:TemplateTriggersState;
	public triggers:TriggersState;
	public privacyExpressionState:ExpressionFieldState;
	public metadataPrivacyExpressionState:ExpressionFieldState;
	public expressionState:ExpressionFieldState;
	public createWorkspaceExpressionState:ExpressionFieldState;

	//Unused
	//-------------
	public DisplayTitle: string = '';
	public ServerRelativeUrl: string = '';
	public NativeMetadataUrl: string = '';
	//-------------

	original: IProvisionProfile = {} as IProvisionProfile;
	availableColumns: IEntityColumn[] = [];
	autoFavouritePeopleFields: EntityMultiColumnSelectorState;
	aiProperties: AIPropertiesListState;
	availableContentTypes: IContentHubType[] = [];
	availableSiteTemplates?: ISiteTemplate[];
	profileInfoOutline: WidgetOutlineState = new WidgetOutlineState('Profile details', 'ℹ️', '#083a8fff');
	workspaceProvisioningOutline: WidgetOutlineState = new WidgetOutlineState('Workspace Provisioning', '💼', '#083a8fff');
	templateTriggersOutline: WidgetOutlineState = new WidgetOutlineState('Templates', '📄', '#083a8fff', false);
	triggersOutline: WidgetOutlineState = new WidgetOutlineState('Triggers', '🔔', '#c99d00ff', false);
	securityOutline: WidgetOutlineState = new WidgetOutlineState('Security', '🔒', '#c99d00ff', false);
	metadataOutline: WidgetOutlineState = new WidgetOutlineState('Metadata', '🏷️', '#c99d00ff', false);
	attachmentsOutline: WidgetOutlineState = new WidgetOutlineState('Attachments', '📎', '#c99d00ff', false);
	favouritesOutline: WidgetOutlineState = new WidgetOutlineState('Favorites & OneDrive', 'OneDrive', '#1d007cff', false);
	aiPropsOutline: WidgetOutlineState = new WidgetOutlineState('List metadata AI', '✨', '#c99d00ff', false);
	sensitivityLabels: ISensitivityLabel[] = [];

	public thisProfile() : IProvisionProfile {
		return {
			Id: this.Id,
			Name: this.Name,
			ProvisionType: this.ProvisionType,
			SiteTemplate: this.SiteTemplate,
			WorkspaceTitle: this.WorkspaceTitle,
			WorkspacePath: this.WorkspacePath,
			DisplayTitle: this.DisplayTitle,
			ServerRelativeUrl: this.ServerRelativeUrl,
			NativeMetadataUrl: this.NativeMetadataUrl,
			TemplateTriggerIds: this.TemplateTriggerIds,
			TriggerIds: this.TriggerIds,
			AIPropertyIds: this.AIPropertyIds,
			SecurityMode: this.SecurityMode,
			SecurityMemberFields: this.SecurityMemberFields,
			SecurityOwnerFields:this.SecurityOwnerFields,
			SecurityVisitorFields:this.SecurityVisitorFields,
			SecurityMembers: this.SecurityMembers,
			SecurityOwners: this.SecurityOwners,
			SecurityVisitors: this.SecurityVisitors,
			PrivacyExpression: this.PrivacyExpression,
			MetadataPrivacyExpression: this.MetadataPrivacyExpression,
			Expression: this.Expression,
			CreateWorkspaceExpression: this.CreateWorkspaceExpression,
			MetadataFields: this.MetadataFields,
			WorkspacePathComponent: this.WorkspacePathComponent,
			FavouritesPeopleFields: this.FavouritesPeopleFields,
			EnableOneDriveSync: this.EnableOneDriveSync,
			OneDriveSyncPath: this.OneDriveSyncPath,
			MoveAttachmentsToWorkspace: this.MoveAttachmentsToWorkspace,
			MoveAttachmentsToWorkspacePath: this.MoveAttachmentsToWorkspacePath,
			ContentTypeIds: this.ContentTypeIds,
			SiteSharingCapabilities: this.SiteSharingCapabilities,
			FavouritesPeople: this.FavouritesPeople,
			SensitivityLabelExpression: this.SensitivityLabelExpression,
			MailNickname: this.MailNickname,
			Description: this.Description,
			MetadataSecurityMode: this.MetadataSecurityMode,
			MetadataSecurityOwnerFields: this.MetadataSecurityOwnerFields,
			MetadataSecurityMemberFields: this.MetadataSecurityMemberFields,
			MetadataSecurityVisitorFields: this.MetadataSecurityVisitorFields,
			MetadataSecurityOwners: this.MetadataSecurityOwners,
			MetadataSecurityMembers: this.MetadataSecurityMembers,
			MetadataSecurityVisitors: this.MetadataSecurityVisitors
		};
	}


	public areTokensLoading: boolean = false;
	public tokens?: IEntityToken[];

	public setShowProfileInfo(show:boolean) {
		this.showProfileInfo = show;
	}

	public setSiteSharingCapabilities(arg0: SharingCapabilities) {
		this.SiteSharingCapabilities = arg0;
	}

	constructor(
		userConfig: IUserConfigPackage,
		entityTypePluralName:string,
		entityType:EntityTypeEditorState,
		profile:IProvisionProfile,
		listUrl:string
	) {
		makeAutoObservable(this);
		this.entityTypePluralName = entityTypePluralName;
		this.userConfig = userConfig;
		this.listUrl = listUrl;

		this.templateTriggers = new TemplateTriggersState(userConfig, (triggerIds) => {
			this.TemplateTriggerIds = triggerIds;
		}, listUrl);

		this.triggers = new TriggersState(userConfig, (triggerIds) => {
			this.TriggerIds = triggerIds;
		}, listUrl);

		this.ownerFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.SecurityOwnerFields = fields;
		});
		
		this.memberFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.SecurityMemberFields = fields;
		});
		
		this.visitorFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.SecurityVisitorFields = fields;
		});
		
		this.metadataSecurityOwnerFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.MetadataSecurityOwnerFields = fields;
		});
		
		this.metadataSecurityMemberFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.MetadataSecurityMemberFields = fields;
		});
		
		this.metadataSecurityVisitorFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.MetadataSecurityVisitorFields = fields;
		});
		
		this.metadataFields = new ContentMetadataFieldsListState(userConfig, (ids) => {
			this.MetadataFields = ids;
		});

		this.privacyExpressionState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, e => {
			this.PrivacyExpression = e;
		});

		this.metadataPrivacyExpressionState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, e => {
			this.MetadataPrivacyExpression = e;
		});

		this.expressionState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, e => {
			this.Expression = e;
		});

		this.createWorkspaceExpressionState = new ExpressionFieldState(ExpressionBuilderOutputType.Boolean, e => {
			this.CreateWorkspaceExpression = e;
		});

		this.autoFavouritePeopleFields = new EntityMultiColumnSelectorState([], FieldType.User, (fields) => {
			this.FavouritesPeopleFields = fields;
		});

		this.aiProperties = new AIPropertiesListState(userConfig, (ids) => {
			this.AIPropertyIds = ids;
		});

		this.setProfile(profile);

		this.templateTriggers.editState.setTemplateApprovalsListUrl(entityType.SharePointTemplateApprovalsListUrl);
	}

	getParentTypeName() {
		if(this.ProvisionType === ProvisionType.Library) {
			return 'Site';
		}

		if(this.ProvisionType === ProvisionType.TeamChannel) {
			return 'Team (ID or site URL)';
		}

		if(this.ProvisionType === ProvisionType.Folder) {
			return 'Library or Folder';
		}
	}
	
	public setAvailableFields(listUrlColumns: import("../../../API").IEntityColumn[]) {
		this.availableColumns = listUrlColumns;
		this.ownerFields.setAvailableFields(listUrlColumns);
		this.memberFields.setAvailableFields(listUrlColumns);
		this.visitorFields.setAvailableFields(listUrlColumns);
		this.metadataSecurityOwnerFields.setAvailableFields(listUrlColumns);
		this.metadataSecurityMemberFields.setAvailableFields(listUrlColumns);
		this.metadataSecurityVisitorFields.setAvailableFields(listUrlColumns);
		this.autoFavouritePeopleFields.setAvailableFields(listUrlColumns);
		this.aiProperties.setAvailableFields(listUrlColumns);
		this.templateTriggers.setAvailableFields(listUrlColumns);
		this.triggers.setAvailableFields(listUrlColumns);
	}

	public setSensitivityLabels(arg0: ISensitivityLabel[]): void {
		this.sensitivityLabels = arg0;
	}


	public setTokens(tokens:IEntityToken[]) {
		this.tokens = tokens;
		this.templateTriggers.setAvailableTokens(tokens);
		this.triggers.setAvailableTokens(tokens);
		this.metadataFields.editState.setTokens(tokens);

		this.expressionState.setAvailableTokens(tokens);
		this.privacyExpressionState.setAvailableTokens(tokens);
		this.metadataPrivacyExpressionState.setAvailableTokens(tokens);
		this.createWorkspaceExpressionState.setAvailableTokens(tokens);
	}

	public selectContentTypeId(id: string, selected: boolean) {
		if(selected) {
			this.ContentTypeIds = this.ContentTypeIds.concat([id]);
		} else {
			this.ContentTypeIds = this.ContentTypeIds.filter(f => f !== id);
		}
	}
	
	public setFavouritesPeople(people: string[]): void {
		this.FavouritesPeople = people;
	}

	private setProfile(profile:IProvisionProfile) {
		this.original = profile;
		Object.assign(this, profile);
		this.setWorkspacePath(profile.WorkspacePath);
		this.setProvisionType(profile.ProvisionType);
		this.ContentTypeIds = (profile.ContentTypeIds || []).slice(0);
		
		this.SecurityOwners = (profile.SecurityOwners || []).slice(0);
		this.SecurityMembers = (profile.SecurityMembers || []).slice(0);
		this.SecurityVisitors = (profile.SecurityVisitors || []).slice(0);

		this.MetadataSecurityOwners = (profile.MetadataSecurityOwners || []).slice(0);
		this.MetadataSecurityMembers = (profile.MetadataSecurityMembers || []).slice(0);
		this.MetadataSecurityVisitors = (profile.MetadataSecurityVisitors || []).slice(0);

		this.FavouritesPeople = (profile.FavouritesPeople || []).slice(0);
		
		// Ensure SensitivityLabelExpression has a default value if not present
		this.SensitivityLabelExpression = profile.SensitivityLabelExpression ?? '';
		
		// Ensure MailNickname has a default value if not present
		this.MailNickname = profile.MailNickname ?? '';
		this.Description = profile.Description ?? '';
		
		this.ownerFields.setSelectedFields(profile.SecurityOwnerFields);
		this.memberFields.setSelectedFields(profile.SecurityMemberFields);
		this.visitorFields.setSelectedFields(profile.SecurityVisitorFields);
		this.metadataSecurityOwnerFields.setSelectedFields(profile.MetadataSecurityOwnerFields);
		this.metadataSecurityMemberFields.setSelectedFields(profile.MetadataSecurityMemberFields);
		this.metadataSecurityVisitorFields.setSelectedFields(profile.MetadataSecurityVisitorFields);
		this.autoFavouritePeopleFields.setSelectedFields(profile.FavouritesPeopleFields);
		this.aiProperties.setSelected(profile.AIPropertyIds);
		this.metadataFields.setSelected(profile.MetadataFields);
		this.templateTriggers.setSelected(profile.TemplateTriggerIds);
		this.triggers.setSelected(profile.TriggerIds);
		this.expressionState.setExpression(profile.Expression);
		this.privacyExpressionState.setExpression(profile.PrivacyExpression);
		this.metadataPrivacyExpressionState.setExpression(profile.MetadataPrivacyExpression);
		this.createWorkspaceExpressionState.setExpression(profile.CreateWorkspaceExpression);
	}
	
	public setOneDriveSyncPath(path: string) {
		this.OneDriveSyncPath = path;
	}
	
	public setOneDriveSync(arg0: boolean) {
		this.EnableOneDriveSync = arg0;
	}

	public setPrivacyExpression(arg0: string) {
		this.PrivacyExpression = arg0;
	}

	public setMetadataPrivacyExpression(arg0: string) {
		this.MetadataPrivacyExpression = arg0;
	}

	public setCreateWorkspaceExpression(arg0: string) {
		this.CreateWorkspaceExpression = arg0;
	}
	
	public setProvisionType(arg0: ProvisionType) {
		this.ProvisionType = arg0;

		if(arg0 === ProvisionType.Site || arg0 === ProvisionType.Team) {
			this.setWorkspacePath('');
		}
	}

	public setSecurityMode(arg0: SecurityMode){
		this.SecurityMode = arg0;
	}

	public setSecurityOwners(people:string[]) {
		this.SecurityOwners = people;
	}

	public setSecurityMembers(people:string[]) {
		this.SecurityMembers = people;
	}

	public setSecurityVisitors(people:string[]) {
		this.SecurityVisitors = people;
	}

	public setMetadataFields(fields:string[]) {
		this.MetadataFields = fields;
	}

	public setListUrl(listUrl:string) {
		this.listUrl = listUrl;
		this.templateTriggers.setListUrl(listUrl);
		this.triggers.setListUrl(listUrl);
	}
	
	public setWorkspaceTitle(title:string) {
		this.WorkspaceTitle = title;
	}
	
	public setName(arg0: string) {
		this.Name = arg0;
	}
	
	public setWorkspacePath(path:string) {
		this.WorkspacePath = path;
	}

	public setWorkspacePathComponent(s:string) {
		this.WorkspacePathComponent = s;
	}

	public setMoveAttachmentsToWorkspace(value: boolean) {
		this.MoveAttachmentsToWorkspace = value;
	}

	public setMoveAttachmentsToWorkspacePath(value: string) {
		this.MoveAttachmentsToWorkspacePath = value;
	}	

	public setSensitivityLabelExpression(value: string) {
		this.SensitivityLabelExpression = value;
	}

	public setMailNickname(value: string) {
		this.MailNickname = value;
	}

	public setDescription(value: string) {
		this.Description = value;
	}

	public setMetadataSecurityMode(arg0: SecurityMode) {
		this.MetadataSecurityMode = arg0;
	}

	public setMetadataSecurityOwners(people: string[]) {
		this.MetadataSecurityOwners = people;
	}

	public setMetadataSecurityMembers(people: string[]) {
		this.MetadataSecurityMembers = people;
	}

	public setMetadataSecurityVisitors(people: string[]) {
		this.MetadataSecurityVisitors = people;
	}

	public setSiteTemplate(template:string) {
		this.SiteTemplate = template;
	}
	public setAvailableSiteTemplates(siteTemplates: ISiteTemplate[]) {
		this.availableSiteTemplates = siteTemplates;
	}
	
	public setAvailableContentTypes(contentTypes:IContentHubType[]) {
		this.availableContentTypes = contentTypes;
	}
	
}

function ProvisionProfileEditor({
	state
}: {
	state: ProvisionProfileEditorState
}) {

	return (

		<>
			{state.showProfileInfo && <>
				<WidgetOutline state={state.profileInfoOutline}>
					<ProfileNameAndCondition state={state} /> 
				</WidgetOutline>
			</>}

			<WidgetOutline state={state.workspaceProvisioningOutline}>
				<ProfileWorkspaceDetails state={state} /> 
			</WidgetOutline>

			<WidgetOutline state={state.templateTriggersOutline}>
				<TemplateTriggers state={state.templateTriggers} />
			</WidgetOutline>

			<WidgetOutline state={state.triggersOutline}>
				<Triggers state={state.triggers} />
			</WidgetOutline>

			<WidgetOutline state={state.securityOutline}>
				<ProfileWorkspaceSecurity state={state} /> 
			</WidgetOutline>

			<WidgetOutline state={state.metadataOutline}>
				<ProfileWorkspaceMetadata state={state} /> 
			</WidgetOutline>

			<WidgetOutline state={state.attachmentsOutline}>
				<ProfileItemAttachments state={state} /> 
			</WidgetOutline>

			<WidgetOutline state={state.favouritesOutline}>
				<EntityTypeDialogFavouritesTab state={state} /> 
			</WidgetOutline>
			
			<WidgetOutline state={state.aiPropsOutline}>
		
				<MessageBar>
					Configure properties which are automatically populated by AI from your workspace document(s) content.
				</MessageBar>
					
				<AIPropertiesList state={state.aiProperties} />
			</WidgetOutline>

		</>
	);
}

export default observer(ProvisionProfileEditor);
